import * as RadixAccordion from '@radix-ui/react-accordion'
import { cx } from 'class-variance-authority'
import type { ComponentProps, ComponentPropsWithoutRef, ReactNode } from 'react'
import { forwardRef } from 'react'

import { Button } from '../Button/Button'

import styles from './Accordion.module.scss'

interface AccordionProps {
  type?: 'single' | 'multiple'
  defaultValue?: string | string[]
  children: ReactNode
  className?: string
  collapsible?: boolean
}

type AccordionItemProps = ComponentPropsWithoutRef<typeof RadixAccordion.Item>
type AccordionTriggerProps = ComponentProps<typeof Button>
type AccordionContentProps = ComponentPropsWithoutRef<
  typeof RadixAccordion.Content
>

const AccordionRoot = ({
  type = 'single',
  defaultValue,
  children,
  className,
  collapsible = false
}: AccordionProps) => {
  const props =
    type === 'multiple'
      ? { type: 'multiple' as const, defaultValue: defaultValue as string[] }
      : {
          type: 'single' as const,
          defaultValue: defaultValue as string,
          collapsible
        }

  return (
    <RadixAccordion.Root
      {...props}
      className={cx(styles.accordionRoot, className)}
    >
      {children}
    </RadixAccordion.Root>
  )
}

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixAccordion.Item
        className={cx(styles.accordionItem, className)}
        ref={ref}
        {...props}
      />
    )
  }
)

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <RadixAccordion.Header className={styles.accordionHeader}>
        <RadixAccordion.Trigger asChild>
          <Button
            className={cx(styles.accordionTrigger, className)}
            ref={ref}
            {...props}
          >
            {children}
          </Button>
        </RadixAccordion.Trigger>
      </RadixAccordion.Header>
    )
  }
)

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <RadixAccordion.Content
        className={cx(styles.accordionContent, className)}
        ref={ref}
        {...props}
      >
        {children}
      </RadixAccordion.Content>
    )
  }
)

AccordionRoot.displayName = 'Accordion.Root'
AccordionItem.displayName = 'Accordion.Item'
AccordionTrigger.displayName = 'Accordion.Trigger'
AccordionContent.displayName = 'Accordion.Content'

export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent
}
